import { FilterType, FilterValue } from '@/models/filter/FilterType';
import type { CubeRawRow } from '@/utils/cube/mapper.type';
import { Filter } from '@cubejs-client/core';
import { Dayjs } from 'dayjs';
import { JSX, ReactElement } from 'react';
import { Location as LocationType } from 'react-router-dom';
import { UserInfo } from '../auth';
import { GetOptionsQueryFunction } from '../cube';
import { Period } from '../date';
import { ArrowPosition, SelectItem } from '../ui';

interface _FilterContext {
  userInfo: UserInfo;
  userCountries?: SelectItem<string>[];
  location?: LocationType;
  isSecondaryLevelFilter?: boolean;
}

type _Evaluator = (params: { options: SelectItem<unknown>[]; contexts: _FilterContext }) => boolean;

type _CubeResultMapper<T> = (value: CubeRawRow, contexts: _FilterContext) => SelectItem<T> | null;

interface _Sortable<T> {
  sort?: (unsortedData: SelectItem<T>[]) => SelectItem<T>[];
}

export type FilterSelectionType =
  | 'dropdown'
  | 'multiselect'
  | 'treeselect'
  | 'oldtreeselect'
  | 'levelselect'
  | 'multilevelselect'
  | 'checkbox';

export type CubeResultMapper<T = string> = _CubeResultMapper<T>;
export type HiddenEvaluator = _Evaluator;
export type DisabledEvaluator = _Evaluator;

export type PlaceholderGetter = (contexts: _FilterContext) => string;
export type TooltipGetter = (contexts: { userInfo: UserInfo }, arrowPosition?: ArrowPosition) => ReactElement;
export type Formatter = (params: {
  value: FilterValue;
  contexts: { userCountries?: SelectItem<string>[] };
  t: (key: string, options?: Record<string, string>) => string;
}) => string | JSX.Element | JSX.Element[];

type StaticFilterOptions<T = string> = _Sortable<T> & {
  options: SelectItem<T>[] | ((params: { contexts: Omit<_FilterContext, 'userInfo'> }) => SelectItem<T>[]);
};

type DynamicFilterOptions<T = string> = _Sortable<T> & {
  sourceCube: string;
  getOptionsQuery: GetOptionsQueryFunction;
  mapResult: CubeResultMapper<T>;
  optionsQuery?: string;
  /**
   * Retains mapped results by source-row identity across query variants and
   * remounts. Enable only when mapResult, processOptions, and sort are pure.
   */
  cacheMappedOptions?: boolean;
  /**
   * Optional post-processing function to transform the list of options.
   * Useful for expanding hierarchical paths or applying custom sorting/filtering logic.
   */
  processOptions?: (options: SelectItem<T>[]) => SelectItem<T>[];
  /**
   * When true, the resolved option paths are registered (and persisted) as the
   * selection is made, then handed to `applyFilter` via
   * `ApplyFilterContext.optionPaths`. Enable for hierarchical filters whose
   * `applyFilter` needs the option universe to build a correct query.
   */
  usesOptionPaths?: boolean;
  /**
   * When true, every resolved option path is recorded (and persisted) in the
   * local option-path store, and handed to `applyFilter` via
   * `ApplyFilterContext.optionPaths`. The local-filter counterpart of
   * `usesOptionPaths`: local filters apply live, so there is nothing to stage.
   */
  recordsOptionPaths?: boolean;
};

export interface FilterConfigBase {
  label: string;
  type: FilterSelectionType;
  placeholder?: string | PlaceholderGetter;
  placeholderFloatingLabel?: string | PlaceholderGetter;
  defaultValue: FilterValue;
  searchable?: boolean;
  hidden?: boolean | HiddenEvaluator;
  disabled?: boolean | DisabledEvaluator;
  clearable?: boolean;
  format?: Formatter;
  comingSoon?: boolean;
  getTooltip?: TooltipGetter;
  collapseSingleChild?: boolean;
  selectionMode?: 'standard' | 'indication';
  applyDynamicFilters?: boolean;
  useFloatingLabel?: boolean;
}

/**
 * Reactive inputs handed to `applyFilter` at call time. Hierarchical filters need
 * the option universe to build their Cube filter; reading it from here (rather
 * than from module-level state that is empty on a fresh page load) keeps the
 * applied query identical to the one rebuilt after a refresh.
 */
export interface ApplyFilterContext {
  /**
   * Option paths the selection was made against, reduced to what the builder
   * reads. Present only for configs that opt in via `usesOptionPaths`.
   */
  optionPaths?: string[];
}

export interface FilterConfigActive {
  applyFilter: (value: FilterValue, context?: ApplyFilterContext) => Filter | undefined;
  filterGroup?: string;
}

export type FilterConfigFixed = FilterConfigBase & FilterConfigActive;
export type FilterConfigPassive<T = string> = FilterConfigBase & StaticFilterOptions<T>;
export type FilterConfigStatic<T = string> = FilterConfigPassive<T> & FilterConfigActive;
export type FilterConfigDynamic<T = string> = FilterConfigFixed & DynamicFilterOptions<T>;

export type FilterConfig<T = string> =
  | FilterConfigFixed
  | FilterConfigStatic<T>
  | FilterConfigDynamic<T>
  | FilterConfigPassive<T>;

/**
 * Value domain of each filter in the global registry. Date-based filters carry
 * richer value types than the string default; listing only those overrides
 * keeps the map minimal while `FilterConfigDomain` falls back to `string` for
 * every other filter.
 */
interface FilterConfigDomainOverrides {
  [FilterType.SINGLE_DATE]: Dayjs;
  [FilterType.DATE_RANGE]: Period;
  [FilterType.COMPARISON_PERIOD]: Period;
}

export type FilterConfigDomain<TType extends FilterType> = TType extends keyof FilterConfigDomainOverrides
  ? FilterConfigDomainOverrides[TType]
  : string;

/**
 * Registry mapping each `FilterType` to a `FilterConfig` typed with that
 * filter's specific value domain, so consumers no longer collapse to `any`.
 */
export type FilterConfigRegistry = {
  [TType in FilterType]: FilterConfig<FilterConfigDomain<TType>>;
};

/**
 * The heterogeneous config type produced by indexing the registry with a
 * runtime `FilterType`. Only date filters carry non-string domains, and they
 * are passive (no `applyFilter`/`getOptionsQuery`), so the active and dynamic
 * guards below narrow to the `string` domain — the only domain those variants
 * ever have at runtime.
 */
export type AnyFilterConfig = FilterConfigRegistry[FilterType];

/**
 * Every passive variant in the registry, preserving each filter's real value
 * domain. Date filters are passive with `Dayjs`/`Period` values, so narrowing
 * to a bare `FilterConfigPassive` (which defaults to `string`) would erase those
 * domains; this union keeps them honest.
 */
export type AnyPassiveFilter = FilterConfigPassive<string> | FilterConfigPassive<Dayjs> | FilterConfigPassive<Period>;

export const isStaticFilter = (filterConfig: AnyFilterConfig): filterConfig is AnyPassiveFilter => {
  return 'options' in filterConfig && filterConfig.options !== undefined;
};

export const isDynamicFilter = (filterConfig: AnyFilterConfig): filterConfig is FilterConfigDynamic => {
  return 'getOptionsQuery' in filterConfig && filterConfig.getOptionsQuery !== undefined;
};

export const isActiveFilter = (filterConfig: AnyFilterConfig): filterConfig is AnyFilterConfig & FilterConfigActive => {
  return 'applyFilter' in filterConfig && filterConfig.applyFilter !== undefined;
};
